import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Dimensions,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import usePreventBodyScroll from 'hooks/use-prevent-body-scroll';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { primitive } from 'styles/constants';
import { isWeb } from 'utils/environment';

import { BottomSheetProps } from './types';

const ANIMATION_DURATION = 250;
const SWIPE_THRESHOLD = 0.3;
const BACKDROP_OPACITY = 0.5;
const WINDOW_HEIGHT = Dimensions.get('window').height;

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  onOpenEventMessage,
  onCloseEventMessage,
  children,
  contentStyle,
  presentationOverlay,
  showDragIndicator = !isWeb,
  closeOnBackdropPress = true,
  testID,
}) => {
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const [isVisible, setIsVisible] = useState(false);
  const contentHeightRef = useRef(0);
  const isClosingRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  usePreventBodyScroll(isVisible);

  const translateY = useSharedValue(WINDOW_HEIGHT);
  const backdropOpacity = useSharedValue(0);

  // shouldCallOnClose=true for user-initiated closes (backdrop, drag, back button).
  // shouldCallOnClose=false when parent drives close via isOpen=false.
  const animateClose = useCallback(
    (shouldCallOnClose: boolean) => {
      if (isClosingRef.current) {
        return;
      }
      isClosingRef.current = true;

      if (shouldCallOnClose) {
        if (onCloseEventMessage) {
          logRBIEvent({
            name: CustomEventNames.ACTION_SHEET_DISMISS,
            type: EventTypes.Other,
            attributes: {
              actionSheetHeader: onCloseEventMessage,
            },
          });
        }
        onClose?.();
      }

      const targetY = contentHeightRef.current || WINDOW_HEIGHT;
      translateY.value = withTiming(targetY, {
        duration: ANIMATION_DURATION,
        easing: Easing.out(Easing.ease),
      });
      backdropOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
      closeTimerRef.current = setTimeout(() => setIsVisible(false), ANIMATION_DURATION);
    },
    [translateY, backdropOpacity, onClose, onCloseEventMessage]
  );

  // React to isOpen changes
  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        // if closed->opened within ANIMATION_DURATION this is needed
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      isClosingRef.current = false;
      setIsVisible(true);

      requestAnimationFrame(() => {
        if (isClosingRef.current) {
          return;
        }

        logRBIEvent({
          name: CustomEventNames.ACTION_SHEET_VIEW,
          type: EventTypes.Other,
          attributes: {
            actionSheetHeader: onOpenEventMessage,
          },
        });

        translateY.value = withTiming(0, { duration: ANIMATION_DURATION });
        backdropOpacity.value = withTiming(BACKDROP_OPACITY, {
          duration: ANIMATION_DURATION,
        });
      });
    } else if (isVisible) {
      animateClose(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset shared values when modal finishes hiding
  useEffect(() => {
    if (!isVisible) {
      isClosingRef.current = false;
      translateY.value = WINDOW_HEIGHT;
      backdropOpacity.value = 0;
    }
  }, [isVisible, translateY, backdropOpacity]);

  const dragHandlePanResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onPanResponderMove: (_, gestureState) => {
          if (gestureState.dy > 0) {
            translateY.value = gestureState.dy;
          }
        },
        onPanResponderRelease: (_, gestureState) => {
          const shouldDismiss =
            gestureState.dy > contentHeightRef.current * SWIPE_THRESHOLD || gestureState.vy > 0.5;

          if (shouldDismiss) {
            runOnJS(animateClose)(true);
          } else {
            translateY.value = withTiming(0, { duration: ANIMATION_DURATION });
          }
        },
      }),
    [translateY, animateClose]
  );

  const sheetAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const handleContentLayout = (event: { nativeEvent: { layout: { height: number } } }) => {
    const { height } = event.nativeEvent.layout;
    if (height > 0) {
      contentHeightRef.current = height;
    }
  };

  const handleBackdropPress = useCallback(() => {
    if (closeOnBackdropPress) {
      animateClose(true);
    }
  }, [closeOnBackdropPress, animateClose]);

  if (!isVisible && !isOpen) {
    return null;
  }

  // A React Native `Modal` opens its own native window on Android, which the single
  // app-level GestureHandlerRootView (see App.tsx) does not extend into. When this sheet
  // carries a presentationOverlay that relies on gesture-handler (e.g. the QA diagnostic
  // launcher), the whole modal's content — sheet, backdrop, and overlay alike — must share
  // one root so a gesture claimed by the overlay can't orphan-intercept touches meant for
  // the sheet or backdrop as siblings outside its bounds.
  const ModalRoot =
    Platform.OS === 'android' && presentationOverlay ? GestureHandlerRootView : View;

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none"
      onRequestClose={() => animateClose(true)}
      statusBarTranslucent
    >
      <ModalRoot style={styles.container} testID={testID}>
        <Animated.View style={[styles.backdrop, backdropAnimatedStyle]}>
          <Pressable style={styles.backdropPressable} onPress={handleBackdropPress} />
        </Animated.View>

        <Animated.View style={[styles.sheetContainer, sheetAnimatedStyle]}>
          <View
            style={[styles.sheet, { paddingBottom: safeAreaBottom || 16 }, contentStyle]}
            onLayout={handleContentLayout}
          >
            {showDragIndicator && (
              <View style={styles.dragIndicatorContainer} {...dragHandlePanResponder.panHandlers}>
                <View style={styles.dragIndicator} />
              </View>
            )}
            {children}
          </View>
        </Animated.View>
        {presentationOverlay}
      </ModalRoot>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
  },
  backdropPressable: {
    flex: 1,
  },
  sheetContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  sheet: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 8,
    minHeight: 100,
    maxHeight: Dimensions.get('window').height * 0.9,
    ...(Platform.OS === 'web' && { overflowY: 'auto' }),
  },
  dragIndicatorContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 40, // Larger touch area for swipe gesture
  },
  dragIndicator: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: primitive.$bbqBrown,
    opacity: 0.3,
  },
});

export default BottomSheet;
